import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@apollo/client'
import FullCalendar from '@fullcalendar/react'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import { setHours, setMinutes, setSeconds, setMilliseconds } from 'date-fns'

import { Button } from '~/ui/button'
import { useSidebar } from '~/ui/sidebar'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '~/ui/select'
import { GraphqlError } from '~/ui/errors'
import { AllPlaylistsQuery, locationsForSelectQuery } from './new-schedule-screen'

// export const sendZoneCommandMutation = gql(`
//   mutation SendZoneCommand($input: SendZoneCommandInput!) {
//     sendZoneCommand(input: $input) {
//       status
//     }
//   }
// `)

export const ScheduleScreen = () => {
  const { setOpen } = useSidebar()
  const calendarRef = useRef(null)
  const CalendarComponent = FullCalendar as any

  const [selectedLocation, setSelectedLocation] = useState<any | null>(null)
  const [selectedZone, setSelectedZone] = useState<any | null>(null)

  useEffect(() => {
    setOpen(false)
    setTimeout(() => {
      const calRef = (calendarRef.current as any).getApi()
      // calRef.render()
      calRef.updateSize()
    }, 300)
    return () => {
      setOpen(true)
    }
  }, [])

  // const [sendZoneCommand] = useSafeMutation(sendZoneCommandMutation)
  const { data, loading, error } = useQuery(locationsForSelectQuery)
  const { data: allPlaylistsData } = useQuery(AllPlaylistsQuery)
  const locations = data?.locationsForSelect ?? []
  const allPlaylists = allPlaylistsData?.allPlaylists ?? []

  useEffect(() => {
    if (!loading && locations.length > 0) {
      setSelectedLocation(locations[0])
      setSelectedZone(locations?.[0]?.zones?.[0])
    }
  }, [locations, loading])

  // const handlePlay = async () => {
  //   const result = await sendZoneCommand({
  //     variables: {
  //       input: {
  //         action: CommandAction.Play,
  //         zoneId: '1',
  //       },
  //     },
  //   })
  //   if (result.data?.sendZoneCommand.status === 'success') {
  //     console.log('Playlist started playing successfully')
  //   } else {
  //     console.error('Failed to start playing playlist')
  //   }
  // }

  const schedulePlaylist = (playlist: any) => {
    if (calendarRef.current) {
      const calendarApi = (calendarRef.current as any).getApi()
      const startTime = setMilliseconds(setSeconds(setMinutes(setHours(new Date(), 9), 0), 0), 0)
      const endTime = setMilliseconds(setSeconds(setMinutes(setHours(new Date(), 17), 0), 0), 0)

      calendarApi.addEvent({
        id: playlist.id,
        title: playlist.name,
        start: startTime,
        end: endTime,
        resourceId: selectedZone?.id,
        allDay: false,
        editable: true,
        startEditable: true,
        durationEditable: true,
      })
    } else {
      console.error('Calendar ref is not set')
    }
  }

  const findPlaylistById = (id: string) => {
    const playlist = allPlaylists.find((playlist: any) => playlist.id === id)
    if (!playlist) {
      console.error(`Playlist with id ${id} not found`)
      return null
    }
    return playlist
  }

  const renderEventContent = (eventInfo: any) => {
    const playlist = findPlaylistById(eventInfo.event.id)
    if (!playlist) {
      return <div>Playlist not found</div>
    }

    return (
      <div className="flex flex-col items-start justify-start gap-0 p-2 font-medium">
        <span className="text-sm">{playlist.name}</span>
        <span className="text-xs">{playlist?.tracksCount} tracks</span>
      </div>
    )
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  return (
    <div className="flex h-full w-full flex-1">
      <div className="flex w-1/4 flex-col border-r">
        <div className="flex items-center justify-between p-3">
          <h1 className="text-2xl">Playlists</h1>
          <Button variant="outline" size="sm" className="cursor-pointer">
            <span className="text-sm">New</span>
          </Button>
        </div>
        <hr className="border-0 border-b" />
        <div className="flex flex-1 flex-col overflow-y-auto">
          {allPlaylists.map((playlist: any) => (
            <div
              key={playlist.id}
              className="flex cursor-pointer items-center justify-between border-b p-3 hover:bg-gray-100"
              onClick={() => schedulePlaylist(playlist)}
            >
              <span>{playlist.name}</span>
              <span className="text-sm text-gray-500">{playlist.tracksCount} tracks</span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-1 flex-col">
        <div className="flex items-center justify-between p-3">
          <h1 className="text-2xl">
            New Schedule
            {selectedZone && selectedLocation ? (
              <span className="text-muted-foreground">
                {' '}
                for {selectedLocation.name} - {selectedZone.name}
              </span>
            ) : (
              ''
            )}
          </h1>
          <div className="flex items-center justify-end gap-2">
            <div className="block">
              <Select value={selectedZone?.id}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select Zone" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectGroup key={location.id}>
                      <SelectLabel>{location.name}</SelectLabel>
                      {location.zones?.map((zone) => (
                        <SelectItem
                          key={zone.id}
                          value={zone.id}
                          onClick={() => {
                            setSelectedLocation(location)
                            setSelectedZone(zone)
                          }}
                        >
                          {zone.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" size="sm" className="cursor-pointer">
              <span className="text-sm">Save</span>
            </Button>
          </div>
        </div>
        <hr className="border-0 border-b" />
        <CalendarComponent
          ref={calendarRef}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'resourceTimeGridDay,resourceTimeGridWeek',
          }}
          plugins={[resourceTimeGridPlugin as any, interactionPlugin as any]}
          initialView="resourceTimeGridWeek"
          resources={[{ id: selectedZone?.id, title: `Zone ${selectedZone?.name}` }]}
          eventContent={renderEventContent}
          events={[]}
          nowIndicator
          selectable
          editable
          eventDrop={(info: any) => {
            /* update via GraphQL */
            // upsert({ variables: { id: info.event.id, attrs: … }});
            console.log('eventDrop', info.event)
          }}
          select={(sel: any) => {
            /* show modal – choose playlist, days, times */
            console.log('select', sel)
          }}
          slotDuration="01:00:00"
          height="auto"
        />
      </div>
    </div>
  )
}
